import {formatPrice} from '@optimaxdev/utils';

import {equals} from 'remeda';

import {AddressType} from 'desktop/types/addressType';
import {StatesItemType} from 'types/locationType';
import {ShippingMethodsListType, ShippingMethodType} from 'types/shippingMethodsType';

type AddressComponentsType = {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    code: string;
    state: string;
    country: string;
};

export type ResumeStructureType = {
    addresses: Array<{
        title: string;
        components: AddressComponentsType;
        isBillingAddress: boolean;
    }>;
    method: string;
};

export type ResumeStructureInputDataType = {
    shippingAddress: AddressType;
    billingAddress: AddressType;
    addressesIsSame: boolean;
    shippingMethods: ShippingMethodsListType;
    currency: string;
    alreadyReceivedStates: {[key: string]: Array<StatesItemType>};
};

type FlatAddressType = {
    [key: string]: string;
};

/**
 * Returns value of selected state if its state received from backend
 * @param {AddressType} address - Address to state value extracting
 * @param {Array<StatesItemType>} alreadyReceivedStates - Already received states list to value comparison
 * @return {string} - State value or empty string
 */
export const getCorrectState = (
    address: AddressType,
    alreadyReceivedStates: {[key: string]: Array<StatesItemType>},
): string => {
    const {
        country: {value: savedCountry},
        regionID: {value: savedRegionID},
        state: {value: savedState},
    } = address;

    const states = alreadyReceivedStates[savedCountry];

    const suitableState = states?.find(state => state.id === String(savedRegionID));
    if (suitableState) return suitableState.value;
    if (savedState) return savedState;
    return '';
};

/**
 * Get address
 * @param {AddressType} address - shipping address
 * @param {string | null} fallbackEmail - Email which we should put instead of received address
 * @param {Array<StatesItemType>} alreadyReceivedStates - Already received states list
 * @return {AddressComponentsType} Address components based on received address
 */
export const getAddressComponents = (
    address: AddressType,
    fallbackEmail: string | null,
    alreadyReceivedStates: {[key: string]: Array<StatesItemType>},
): AddressComponentsType => ({
    address: address.address.value,
    city: address.city.value,
    code: address.postal.value,
    country: address.country.value,
    email: fallbackEmail || address.email.value,
    name: `${address.firstName.value} ${address.lastName.value}`,
    phone: address.phone.value,
    state: getCorrectState(address, alreadyReceivedStates),
});

/**
 * Get delivery method components join to one string
 * @param {ShippingMethodsListType} shippingMethods - Received shipping methods
 * @param {string} currency - Received currency symbol
 * @return {string} Formatted delivery method string
 */
export const getDeliveryComponents = (
    shippingMethods: ShippingMethodsListType,
    currency: string,
): string => {
    const {list, selected} = shippingMethods;
    const selectedMethod = list.find(
        (method: ShippingMethodType): boolean => method.code === selected,
    );
    if (selectedMethod) {
        const {description, price, title} = selectedMethod;
        const processedPrice = Number(price) ? formatPrice(currency, price) : 'Free';
        return `${title}, ${description} (${processedPrice})`;
    }

    return '';
};

/**
 * Extract address values to make address entity flatter
 * @param {AddressType} address - Address to extracting values form
 * @return {FlatAddressType} Flat address entity
 */
export const extractAddressValues = (address: AddressType): FlatAddressType => {
    const values: {[key: string]: string} = {};
    (Object.keys(address) as (keyof AddressType)[]).forEach(key => {
        values[key] = address[key].value;
    });
    return values;
};

/**
 * Get content structure for completed section of first step
 * @param {ResumeStructureInputDataType} address - Input data to form structure
 * @param {string} fallbackEmail - Fallback email value in case of any address will not contain email value
 * @return {ResumeStructureType} Returned completed section content structure
 */
export const getResumeStructure = (
    {
        addressesIsSame,
        alreadyReceivedStates,
        billingAddress,
        currency,
        shippingAddress,
        shippingMethods,
    }: ResumeStructureInputDataType,
    fallbackEmail: string | null,
): ResumeStructureType => {
    const shipping = extractAddressValues(shippingAddress);
    const billing = extractAddressValues(billingAddress);
    const addressesIsReallySame = equals(shipping, billing);

    if (addressesIsSame || addressesIsReallySame) {
        return {
            addresses: [
                {
                    components: getAddressComponents(
                        shippingAddress,
                        fallbackEmail,
                        alreadyReceivedStates,
                    ),
                    isBillingAddress: false,
                    title: 'Delivery',
                },
            ],
            method: getDeliveryComponents(shippingMethods, currency),
        };
    }
    return {
        addresses: [
            {
                components: getAddressComponents(
                    shippingAddress,
                    fallbackEmail,
                    alreadyReceivedStates,
                ),
                isBillingAddress: false,
                title: 'Delivery',
            },
            {
                components: getAddressComponents(
                    billingAddress,
                    fallbackEmail,
                    alreadyReceivedStates,
                ),
                isBillingAddress: true,
                title: 'Billing',
            },
        ],
        method: getDeliveryComponents(shippingMethods, currency),
    };
};
